/* ========================================
   COLLECTION FILTER SYSTEM - CONSOLIDATED JS.
   ======================================== */

   (function() {
    'use strict';
  
    // ========================================
    // CONFIGURATION & UTILITIES
    // ========================================
    // Moved to filter-utils.js - access via window.FilterUtils and window.CacheManager
    
    // Access utilities from global scope (exposed by filter-utils.js)
    const CONFIG = window.FilterUtils?.CONFIG;
    const Utils = window.FilterUtils?.Utils;
    const UrlUtils = window.FilterUtils?.UrlUtils;
    const CacheManager = window.CacheManager;

    if (!CONFIG || !Utils || !CacheManager) {
      console.error('[collection-filter.js] Dependencies not found. Ensure filter-utils.js is loaded first.');
      return;
    }

    // ========================================
    // OPTIMIZED AJAX HANDLER
    // ========================================
    
    const AjaxHandler = {
      activeRequests: new Map(),
      requestTimeout: 8000,
      maxRetries: 2,
      
      async fetchWithRetry(url, options = {}, retries = AjaxHandler.maxRetries) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), AjaxHandler.requestTimeout);
        
        try {
          const response = await fetch(url, {
            ...options,
            signal: controller.signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'text/html,application/xhtml+xml',
              'Cache-Control': 'no-cache',
              ...options.headers
            }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          return response;
        } catch (error) {
          clearTimeout(timeoutId);
          
          if (retries > 0 && !controller.signal.aborted) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            return AjaxHandler.fetchWithRetry(url, options, retries - 1);
          }
          
          throw error;
        }
      },
      
      async getFilteredContent(url, useCache = true) {
        // Check if request is already in progress
        if (AjaxHandler.activeRequests.has(url)) {
          return AjaxHandler.activeRequests.get(url);
        }
        
        // Check cache first
        if (useCache) {
          const cached = CacheManager.get(url);
          if (cached) {
            return Promise.resolve(cached);
          }
        }
        
        // Create new request
        const requestPromise = AjaxHandler.fetchWithRetry(url)
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Prefer .collection__main (main grid) so we don't get a carousel with .product-list
            const result = {
              html,
              doc,
              collection: doc.querySelector('.collection'),
              products: doc.querySelector('.collection__main') || doc.querySelector('.collection__products') || doc.querySelector('.product-list') || doc.querySelector('[data-products]'),
              filters: doc.querySelector('.collection-filter'),
              pagination: doc.querySelector('.pagination, [data-pagination]'),
              productCount: doc.querySelector('.collection-header__count, [data-product-count]'),
              url
            };
            
            // Pre-process and optimize content
            if (result.products) {
              // Remove any script tags from products for security
              const scripts = result.products.querySelectorAll('script');
              scripts.forEach(script => script.remove());
              
              // Lazy load images in the background
              const images = result.products.querySelectorAll('img[data-src]');
              images.forEach(img => {
                if (img.dataset.src) {
                  Utils.preloadResource(img.dataset.src, 'image');
                }
              });
            }
            
            // Cache the result
            if (useCache) {
              CacheManager.set(url, result);
            }
            
            return result;
          })
          .finally(() => {
            AjaxHandler.activeRequests.delete(url);
          });
        
        AjaxHandler.activeRequests.set(url, requestPromise);
        return requestPromise;
      },

      /**
       * Get next page URL from result's pagination (rel="next" link), or null.
       * @param {{ doc: Document, pagination: Element|null }} result
       * @returns {string|null}
       */
      getNextPageUrl(result) {
        const pagination = result.pagination || (result.doc && result.doc.querySelector('.pagination, [data-pagination]'));
        if (!pagination) return null;
        const nextLink = pagination.querySelector('a[rel="next"][href*="page="]');
        if (!nextLink) return null;
        const href = nextLink.getAttribute('href');
        if (!href) return null;
        try {
          return new URL(href, window.location.origin).pathname + new URL(href, window.location.origin).search;
        } catch {
          return href.startsWith('/') ? href : (window.location.pathname + (href.startsWith('?') ? href : '?' + href));
        }
      },

      /**
       * Get max page number from a result's pagination (from all page= links). Used when next link is missing.
       */
      getTotalPagesFromResult(result) {
        const pagination = result.pagination || (result.doc && result.doc.querySelector('.pagination, [data-pagination]'));
        if (!pagination) return 1;
        let maxPage = 1;
        pagination.querySelectorAll('a[href*="page="]').forEach(a => {
          const match = (a.getAttribute('href') || '').match(/[?&]page=(\d+)/);
          if (match) {
            const n = parseInt(match[1], 10);
            if (n > maxPage) maxPage = n;
          }
        });
        return maxPage;
      },

      /**
       * Count product cards in a result (for knowing when to stop fetching pages).
       */
      countProductCardsInResult(result) {
        if (!result || !result.products) return 0;
        const sel = CONFIG.selectors.productCard || 'product-card, .product-card, [data-product-card], .card';
        return (result.products.querySelectorAll && result.products.querySelectorAll(sel).length) || 0;
      },

      /** Max concurrent requests to the same domain (browser limit ~6). Use all slots for faster loading. */
      MAX_CONCURRENT_REQUESTS: 6,

      /**
       * Fetch all pages for one tag URL and return an array of result objects (one per page).
       * Uses rel="next" or page= links when present; otherwise keeps requesting page=2,3,... until a page has no products.
       */
      async getAllPagesForTag(baseUrl) {
        const first = await AjaxHandler.getFilteredContent(baseUrl, false);
        if (!first || !first.products) return [first].filter(Boolean);
        const results = [first];
        let nextUrl = this.getNextPageUrl(first);
        while (nextUrl) {
          const nextResult = await AjaxHandler.getFilteredContent(nextUrl, false);
          if (!nextResult || !nextResult.products) break;
          results.push(nextResult);
          nextUrl = this.getNextPageUrl(nextResult);
        }
        if (results.length > 1) return results;
        const totalPages = this.getTotalPagesFromResult(first);
        if (totalPages > 1) {
          const sep = baseUrl.includes('?') ? '&' : '?';
          for (let page = 2; page <= totalPages; page++) {
            const pageUrl = baseUrl.includes('page=') ? baseUrl.replace(/([?&]page=)\d+/, '$1' + page) : baseUrl + sep + 'page=' + page;
            const nextResult = await AjaxHandler.getFilteredContent(pageUrl, false);
            if (nextResult && nextResult.products) results.push(nextResult);
          }
          return results;
        }
        // No pagination in response: fetch page=2, page=3, ... until a page has no products
        const productCardSelector = CONFIG.selectors.productCard || 'product-card, .product-card, [data-product-card], .card';
        let page = 2;
        for (;;) {
          const sep = baseUrl.includes('?') ? '&' : '?';
          const pageUrl = baseUrl + sep + 'page=' + page;
          const nextResult = await AjaxHandler.getFilteredContent(pageUrl, false);
          if (!nextResult || !nextResult.products) break;
          const cards = nextResult.products.querySelectorAll && nextResult.products.querySelectorAll(productCardSelector);
          if (!cards || cards.length === 0) break;
          results.push(nextResult);
          page++;
        }
        return results;
      },

      /**
       * Fetch all remaining pages (page 2+) across multiple tags with up to 6 concurrent requests.
       * Uses browser's ~6-connection-per-domain limit. firstPageResults = page 1 per tag (already fetched).
       * @param {Object[]} firstPageResults - one result per tag (page 1)
       * @param {string[]} baseUrls - base URL per tag (for building page=2 when rel="next" is missing)
       * @param {number} concurrency - max simultaneous requests (default 6)
       * @returns {Promise<Object[]>} all page results (firstPageResults + additional pages)
       */
      async fetchRemainingPagesWithConcurrency(firstPageResults, baseUrls, concurrency) {
        const limit = Math.max(1, concurrency || AjaxHandler.MAX_CONCURRENT_REQUESTS);
        const queue = [];
        firstPageResults.forEach((r, tagIndex) => {
          let nextUrl = AjaxHandler.getNextPageUrl(r);
          if (!nextUrl && baseUrls && baseUrls[tagIndex] && AjaxHandler.getTotalPagesFromResult(r) > 1) {
            const base = baseUrls[tagIndex];
            nextUrl = base.includes('?') ? base.replace(/([?&])page=\d+/, '$1page=2') : base + (base.includes('?') ? '&' : '?') + 'page=2';
          }
          if (nextUrl) queue.push({ url: nextUrl, tagIndex });
        });
        if (queue.length === 0) return firstPageResults;

        const results = firstPageResults.slice();
        let inFlight = 0;

        const processOne = (item) => {
          inFlight++;
          return AjaxHandler.getFilteredContent(item.url, false)
            .then((result) => {
              if (result && result.products) {
                results.push(result);
                const nextUrl = AjaxHandler.getNextPageUrl(result);
                if (nextUrl) queue.push({ url: nextUrl, tagIndex: item.tagIndex });
              }
            })
            .catch(() => {})
            .finally(() => {
              inFlight--;
              drain();
            });
        };

        return new Promise((resolve) => {
          const drain = () => {
            while (queue.length > 0 && inFlight < limit) {
              const item = queue.shift();
              processOne(item);
            }
            if (queue.length === 0 && inFlight === 0) resolve(results);
          };
          drain();
        });
      },

      /** Products per page when showing OR-merged results (client-side pagination). */
      OR_RESULTS_PER_PAGE: 12,

      /**
       * Build pagination HTML for OR-merged results (theme-compatible nav).
       * @param {number} totalItems
       * @param {number} currentPage
       * @param {string} basePath - path without query (e.g. /collections/all-products/color_beige+color_black)
       * @param {string|URL} referenceUrl - source for preserved query params (view, sort_by, etc.)
       */
      buildORPaginationHTML(totalItems, currentPage, basePath, referenceUrl) {
        const perPage = this.OR_RESULTS_PER_PAGE;
        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        currentPage = Math.max(1, Math.min(currentPage, totalPages));
        if (totalPages <= 1) return '';
        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        const ref = referenceUrl || (typeof window !== 'undefined' ? window.location.href : basePath);
        const pageUrl = (p) => {
          if (UrlUtils) {
            return UrlUtils.buildUrlWithAllQuery(basePath, ref, { page: p });
          }
          const sep = basePath.includes('?') ? '&' : '?';
          return p <= 1 ? basePath : basePath + sep + 'page=' + p;
        };
        const prevUrl = currentPage <= 1 ? null : pageUrl(currentPage - 1);
        const nextUrl = currentPage >= totalPages ? null : pageUrl(currentPage + 1);
        let parts = [];
        if (prevUrl) {
          parts.push(`<a class="pagination__link h6" rel="prev" href="${prevUrl}">`);
          parts.push('<svg xmlns="http://www.w3.org/2000/svg" width="11" viewBox="0 0 11 20" fill="none" aria-hidden="true"><path d="M9 2L2 10l7 8" stroke="currentColor" stroke-width="2"/></svg></a>');
        }
        const showPages = 5;
        let start = Math.max(1, currentPage - Math.floor(showPages / 2));
        let end = Math.min(totalPages, start + showPages - 1);
        if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);
        for (let i = start; i <= end; i++) {
          if (i === currentPage) {
            parts.push(`<span class="pagination__link pagination__link--disabled h6" aria-current="page">${i}</span>`);
          } else {
            parts.push(`<a class="pagination__link h6" href="${pageUrl(i)}">${i}</a>`);
          }
        }
        if (nextUrl) {
          parts.push(`<a class="pagination__link h6" rel="next" href="${nextUrl}">`);
          parts.push('<svg xmlns="http://www.w3.org/2000/svg" width="11" viewBox="0 0 11 20" fill="none" aria-hidden="true"><path d="M2 2l7 8-7 8" stroke="currentColor" stroke-width="2"/></svg></a>');
        }
        return `<nav class="pagination" role="navigation" aria-label="Pagination">${parts.join('')}</nav>`;
      },

      /**
       * Build the "Load more" UI for OR-merged results (mirrors snippets/collection-load-more.liquid).
       * The button only re-plays the hidden next link, which buildGlobalEvents' pagination handler
       * intercepts to render one more page worth of merged results.
       * @param {number} totalItems
       * @param {number} currentPage
       * @param {string} basePath - path without query (e.g. /collections/all-products/color_beige+color_black)
       * @param {string|URL} referenceUrl - source for preserved query params (view, sort_by, etc.)
       */
      buildORLoadMoreHTML(totalItems, currentPage, basePath, referenceUrl) {
        const perPage = this.OR_RESULTS_PER_PAGE;
        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        if (totalPages <= 1) return '';
        currentPage = Math.max(1, Math.min(currentPage, totalPages));

        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        const ref = referenceUrl || (typeof window !== 'undefined' ? window.location.href : basePath);
        const nextPage = currentPage + 1;
        let nextUrl = null;
        if (nextPage <= totalPages) {
          nextUrl = UrlUtils
            ? UrlUtils.buildUrlWithAllQuery(basePath, ref, { page: nextPage })
            : basePath + (basePath.includes('?') ? '&' : '?') + 'page=' + nextPage;
        }

        const shownCount = Math.min(currentPage * perPage, totalItems);
        const existingLabel = document.querySelector('.collection-load-more__label');
        const label = (existingLabel && existingLabel.textContent.trim()) || 'Load More';

        const countHTML = `<p class="collection-load-more__count" aria-live="polite">Showing <span data-load-more-shown>${shownCount}</span> of ${totalItems} products</p>`;
        const buttonHTML = nextUrl
          ? `<button type="button" class="collection-load-more__button" data-load-more-button><span class="collection-load-more__label">${label}</span><span class="collection-load-more__spinner"></span></button>` +
            `<a rel="next" href="${nextUrl}" hidden aria-hidden="true" tabindex="-1"></a>`
          : '';

        return `<collection-load-more class="collection-load-more" data-mode="or" data-current-page="${currentPage}" data-total-pages="${totalPages}" data-page-size="${perPage}" data-total-items="${totalItems}">${countHTML}${buttonHTML}</collection-load-more>`;
      },

      /** Load more is only in play when the section shipped its custom element (merchant setting). */
      isLoadMoreEnabled() {
        return !!(window.customElements && window.customElements.get('collection-load-more'));
      },

      /**
       * Items to render for `page`: every page up to it with load more, that page alone with pagination.
       */
      sliceORItemsForPage(items, page, perPage) {
        const start = AjaxHandler.isLoadMoreEnabled() ? 0 : (page - 1) * perPage;
        return items.slice(start, page * perPage);
      },

      /** Navigation rendered under the OR-merged grid. */
      buildORNavigationHTML(totalItems, currentPage, basePath, referenceUrl) {
        return AjaxHandler.isLoadMoreEnabled()
          ? AjaxHandler.buildORLoadMoreHTML(totalItems, currentPage, basePath, referenceUrl)
          : AjaxHandler.buildORPaginationHTML(totalItems, currentPage, basePath, referenceUrl);
      },

      /**
       * Get a stable key for dedupe. Prefer IDs, then normalized href (O(1) lookup via Set).
       */
      getProductDedupeKey(card, fallbackIndex) {
        const id = card.getAttribute && (card.getAttribute('data-product-id') || card.getAttribute('data-id'));
        if (id) return String(id);
        const link = card.querySelector && card.querySelector('a[href*="/products/"]');
        if (link) {
          const href = link.getAttribute('href') || '';
          const path = href.split('?')[0];
          return path ? path.toLowerCase() : 'html-' + fallbackIndex;
        }
        return 'html-' + fallbackIndex;
      },

      /**
       * Extract product card HTML and merge with dedupe. Uses Set for O(1) duplicate checks (no list search).
       * @param {{ products: Element }} result
       * @param {Set<string>} seenKeys - Set for O(1) has/add; reuse cache.seenKeys to avoid rebuilding from HTML
       * @param {string[]} productHtmlParts
       */
      mergeProductCardsFromResult(result, seenKeys, productHtmlParts) {
        const productCardSelector = CONFIG.selectors.productCard || 'product-card, .product-card, [data-product-card], .card';
        const container = result.products;
        if (!container) return;
        const cards = container.querySelectorAll(productCardSelector);
        cards.forEach(card => {
          const dedupeKey = AjaxHandler.getProductDedupeKey(card, productHtmlParts.length);
          if (seenKeys.has(dedupeKey)) return;
          seenKeys.add(dedupeKey);
          productHtmlParts.push(card.outerHTML);
        });
      },

      /**
       * Fetch first page of each tag in parallel, show first 12 immediately, then load remaining pages in background.
       * @param {string[]} urls - single-tag URLs (page 1)
       * @param {{ basePath?: string, currentPage?: number }} options
       */
      async getFilteredContentOR(urls, options = {}) {
        if (!urls.length) return null;
        if (urls.length === 1) return AjaxHandler.getFilteredContent(urls[0], false);

        // Fetch first page of each tag in parallel – show results immediately
        const firstPageResults = await Promise.all(urls.map(url => AjaxHandler.getFilteredContent(url, false)));
        const first = firstPageResults.find(r => r && r.products) || firstPageResults[0];
        if (!first || !first.products) return first;

        const seenKeys = new Set();
        const productHtmlParts = [];
        firstPageResults.forEach(result => AjaxHandler.mergeProductCardsFromResult(result, seenKeys, productHtmlParts));
 
        const tagHasMore = firstPageResults.map(result => {
          if (!result || !result.products) return false;
          const nextUrl = AjaxHandler.getNextPageUrl(result);
          const totalPages = AjaxHandler.getTotalPagesFromResult(result);
          return !!nextUrl || totalPages > 1;
        });

        const perPage = AjaxHandler.OR_RESULTS_PER_PAGE;
        const basePath = options.basePath || (typeof window !== 'undefined' && window.location ? window.location.pathname : '');
        let currentPage = Math.max(1, parseInt(options.currentPage || 1, 10));
        const totalCount = productHtmlParts.length;
        const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
        currentPage = Math.min(currentPage, totalPages);
        const pageParts = AjaxHandler.sliceORItemsForPage(productHtmlParts, currentPage, perPage);
        const mergedCardsHTML = pageParts.join('\n');

        const productListEl = first.products.querySelector && first.products.querySelector('product-list');
        let mergedProductsHTML = mergedCardsHTML;
        if (productListEl) {
          const mobileLayout = productListEl.getAttribute('collection-mobile-layout') || 'medium';
          const desktopLayout = productListEl.getAttribute('collection-desktop-layout') || 'medium';
          mergedProductsHTML = `<product-list class="product-list" collection-mobile-layout="${mobileLayout}" collection-desktop-layout="${desktopLayout}">${mergedCardsHTML}</product-list>`;
        }

        const referenceUrl = options.referenceUrl || (typeof window !== 'undefined' ? window.location.href : '');
        const paginationHTML = totalPages > 1 ? AjaxHandler.buildORNavigationHTML(totalCount, currentPage, basePath, referenceUrl) : '';

        if (typeof window !== 'undefined') {
          window.__filterORCache = {
            basePath,
            referenceUrl,
            products: Array.from(productHtmlParts),
            total: totalCount,
            perPage,
            tagUrls: Array.from(urls),
            tagPageNumbers: urls.map(() => 1),
            tagHasMore: Array.from(tagHasMore),
            seenKeys: new Set(seenKeys)
          };

          // Load remaining pages in background (up to 6 concurrent requests per domain); update cache when done
          if (tagHasMore.some(Boolean)) {
            AjaxHandler.fetchRemainingPagesWithConcurrency(firstPageResults, urls, AjaxHandler.MAX_CONCURRENT_REQUESTS)
              .then((allPageResults) => {
                const seen = new Set();
                const fullList = [];
                allPageResults.forEach(result => AjaxHandler.mergeProductCardsFromResult(result, seen, fullList));
                const cache = window.__filterORCache;
                if (cache && cache.basePath === basePath && cache.tagUrls && cache.tagUrls.length === urls.length) {
                  cache.products = fullList;
                  cache.total = fullList.length;
                  cache.tagHasMore = urls.map(() => false);
                  cache.seenKeys = seen;
                }
              })
              .catch((error) => {
                Utils.error('Background fetch failed:', error);
                if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
                  console.warn('[Filter] Background fetch incomplete - results may be partial');
                }
                if (error && error.message && error.message.indexOf('429') !== -1) {
                  console.warn('[Filter] Shopify rate limit hit - some products may not load');
                }
              });
          }
        }

        const productsBlockHTML = paginationHTML ? mergedProductsHTML + '\n' + paginationHTML : mergedProductsHTML;

        return {
          ...first,
          html: first.html,
          doc: first.doc,
          collection: null,
          products: { innerHTML: productsBlockHTML },
          filters: first.filters,
          pagination: { innerHTML: paginationHTML },
          productCount: { textContent: String(totalCount) },
          url: first.url
        };
      },

      /**
       * Fetch more tag pages until cache has enough products for targetPage (for OR direct load / page > 1).
       * @param {Object} cache - __filterORCache
       * @param {number} targetPage - 1-based page number
       */
      async ensureORCacheHasPage(cache, targetPage) {
        if (!cache || !cache.tagUrls || !cache.tagHasMore) return;
        const neededCount = targetPage * cache.perPage;
        let seenKeys = cache.seenKeys;
        if (!seenKeys || !(seenKeys instanceof Set)) {
          seenKeys = new Set();
          cache.products.forEach((html, i) => {
            const el = document.createElement('div');
            el.innerHTML = html;
            const node = el.firstElementChild || el;
            seenKeys.add(AjaxHandler.getProductDedupeKey(node, i));
          });
          cache.seenKeys = seenKeys;
        }
        while (cache.products.length < neededCount && cache.tagHasMore.some(Boolean)) {
          const urlsToFetch = [];
          for (let i = 0; i < cache.tagUrls.length; i++) {
            if (!cache.tagHasMore[i]) continue;
            const base = cache.tagUrls[i];
            const nextPage = (cache.tagPageNumbers[i] || 1) + 1;
            const UrlUtilsRef = window.FilterUtils && window.FilterUtils.UrlUtils;
            const tagPath = base.split('?')[0];
            const pageUrl = UrlUtilsRef
              ? UrlUtilsRef.buildUrlWithAllQuery(tagPath, base, { page: nextPage })
              : (base.includes('?') ? base.replace(/([?&])page=\d+/, '$1page=' + nextPage) : base + (base.includes('?') ? '&' : '?') + 'page=' + nextPage);
            urlsToFetch.push({ index: i, url: pageUrl });
          }
          const results = await Promise.all(urlsToFetch.map(({ url }) => AjaxHandler.getFilteredContent(url, false)));
          results.forEach((result, idx) => {
            const i = urlsToFetch[idx].index;
            if (!result || !result.products) {
              cache.tagHasMore[i] = false;
              return;
            }
            AjaxHandler.mergeProductCardsFromResult(result, seenKeys, cache.products);
            cache.tagPageNumbers[i] = (cache.tagPageNumbers[i] || 1) + 1;
            const nextUrl = AjaxHandler.getNextPageUrl(result);
            const totalPages = AjaxHandler.getTotalPagesFromResult(result);
            cache.tagHasMore[i] = !!nextUrl || totalPages > cache.tagPageNumbers[i];
          });
          if (results.every(r => !r || !r.products)) break;
        }
      },

      /**
       * Build result shape (products + pagination HTML) from OR cache for a given page.
       * @param {Object} cache - __filterORCache
       * @param {number} currentPage - 1-based
       * @param {Object} firstResult - result from getFilteredContentOR (for productListEl attrs)
       */
      buildORResultFromCache(cache, currentPage, firstResult) {
        cache.total = cache.products.length;
        const totalPages = Math.max(1, Math.ceil(cache.total / cache.perPage));
        const safePage = Math.min(currentPage, totalPages);
        const pageParts = AjaxHandler.sliceORItemsForPage(cache.products, safePage, cache.perPage);
        const mergedCardsHTML = pageParts.join('\n');
        const productListEl = firstResult.doc && firstResult.doc.querySelector && firstResult.doc.querySelector('.collection__main product-list, .collection__main .product-list, product-list');
        let mergedProductsHTML = mergedCardsHTML;
        if (productListEl) {
          const mobileLayout = productListEl.getAttribute('collection-mobile-layout') || 'medium';
          const desktopLayout = productListEl.getAttribute('collection-desktop-layout') || 'medium';
          mergedProductsHTML = `<product-list class="product-list" collection-mobile-layout="${mobileLayout}" collection-desktop-layout="${desktopLayout}">${mergedCardsHTML}</product-list>`;
        }
        const paginationHTML = totalPages > 1 ? AjaxHandler.buildORNavigationHTML(cache.total, safePage, cache.basePath, cache.referenceUrl) : '';
        const productsBlockHTML = paginationHTML ? mergedProductsHTML + '\n' + paginationHTML : mergedProductsHTML;
        return {
          ...firstResult,
          products: { innerHTML: productsBlockHTML },
          pagination: { innerHTML: paginationHTML },
          productCount: { textContent: String(cache.total) }
        };
      },

      // Cancel all active requests
      cancelAllRequests: function() {
        for (const [url, request] of AjaxHandler.activeRequests.entries()) {
          if (request.controller) {
            request.controller.abort();
          }
        }
        AjaxHandler.activeRequests.clear();
      }
    };

    // Expose AjaxHandler globally for filter-ajax.js
    window.AjaxHandler = AjaxHandler;

    // ========================================
    // FILTER OVERLAY MANAGEMENT
    // ========================================
    // Moved to filter-ui.js - access via window.FilterOverlay
    
    // ========================================
    // SORT FUNCTIONALITY
    // ========================================
    
    const SortManager = {
      init() {
        const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
        if (sortSelect) {
          sortSelect.addEventListener('change', this.handleSortChange.bind(this));
        } else {
          Utils.error('Sort select element not found with selector: ' + CONFIG.selectors.sortSelect);
        }
      },
  
      handleSortChange(event) {
        const valueSelected = event.target.value;
        const mainUrl = UrlUtils.buildSortUrl(valueSelected, window.location.href);

        // Update selected filters display before navigation
        if (typeof window.SelectedFiltersManager !== 'undefined') {
          window.SelectedFiltersManager.updateDisplay();
        }
        
        // Use requestAnimationFrame before navigation for smooth transition
        requestAnimationFrame(() => {
          location.href = mainUrl;
        });
      }
    };
  
    // ========================================
    // FILTER BAR FUNCTIONALITY
    // ========================================
    
    const FilterBar = {
      init() {
        this.initializeFilterBar();
        this.initializeFilterBarComponents();
        this.bindEvents();
      },
  
      initializeFilterBar() {
        const container = document.querySelector(CONFIG.selectors.filterBarContainer);
        if (!container) return;
  
        let filterValue = Utils.getLocalStorage(CONFIG.localStorageKeys.filterValue);
        
        if (filterValue && window.location.href.indexOf(filterValue) !== -1) {
          const filterElement = document.getElementById(`filter-${filterValue.replace(" ", "&")}`);
          if (filterElement) {
            filterElement.classList.add('active--filters');
          }
        }
      },
  
      initializeFilterBarComponents() {
        const container = document.querySelector(CONFIG.selectors.filterBarContainer);
        if (container) {
          container.style.display = 'block';
          container.classList.add('filter-tab-open');
        }
        
        const carousel = document.querySelector(CONFIG.selectors.filterBarCarousel);
        if (carousel) {
          carousel.style.opacity = '1';
        }
  
        this.filterCollectionBoxes = document.querySelectorAll(CONFIG.selectors.filterCollectionBoxes);
      },
  
      bindEvents() {
        this.filterCollectionBoxes = document.querySelectorAll(CONFIG.selectors.filterCollectionBoxes);
        this.filterCollectionBoxes.forEach((box) => {
          box.addEventListener('click', this.handleFilterBoxClick.bind(this));
        });
      },
  
      async handleFilterBoxClick(event) {
        const elem = event.currentTarget;
        const collectionHandle = elem.dataset.collectionHandle || this.extractCollectionHandle();
        const filterValue = elem.dataset.filterValue || elem.textContent.trim();
        const filterId = elem.dataset.id || elem.textContent.trim();

        // Show loading overlay for tab filters
        if (typeof window.LoadingManager !== 'undefined') {
          window.LoadingManager.showCollectionLoading();
        }
  
        if (elem.classList.contains('active--filters')) {
          await this.removeFilter(elem, collectionHandle);
        } else {
          await this.applyFilter(elem, collectionHandle, filterValue, filterId);
        }
      },
  
      async removeFilter(elem, collectionHandle) {
        // Remove active class from all filters
        this.filterCollectionBoxes.forEach(el => {
          el.classList.remove("active--filters");
        });
  
        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        const newUrl = UrlUtils
          ? UrlUtils.buildUrlWithPreservedQuery(`/collections/${collectionHandle}`, window.location.href)
          : `/collections/${collectionHandle}`;

        try {
          // Update browser URL without page reload
          window.history.replaceState(null, null, newUrl);
          
          const response = await fetch(newUrl);
          
          if (response.ok) {
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Find existing collection element
            const existingCollection = document.querySelector('.collection');
            if (existingCollection) {
              // Get new collection content
              const newCollectionContent = doc.getElementsByClassName("collection")[0];
              if (newCollectionContent) {
                
                // Try to preserve hero section and only update products
                const existingHero = existingCollection.querySelector('.collection-hero-top, .collection__hero, [class*="hero"]');
                const existingProducts = existingCollection.querySelector('.collection__main, .collection__products, .product-list, [class*="products"]');
                
                const newProducts = newCollectionContent.querySelector('.collection__main, .collection__products, .product-list, [class*="products"]');
                
                if (existingProducts && newProducts && existingHero) {
                  // Selective update: only replace products section, preserve hero
                  existingProducts.innerHTML = newProducts.innerHTML;
                } else {
                  // Fallback: full replacement but try to preserve hero
                  if (existingHero) {
                    // Save hero content
                    const heroHTML = existingHero.outerHTML;
                    
                    // Replace entire collection content
                    existingCollection.innerHTML = newCollectionContent.innerHTML;
                    
                    // Try to restore hero at the beginning
                    const newHeroLocation = existingCollection.querySelector('.collection-hero-top, .collection__hero, [class*="hero"]');
                    if (!newHeroLocation) {
                      // Insert hero at the beginning if it doesn't exist in new content
                      existingCollection.insertAdjacentHTML('afterbegin', heroHTML);
                    }
                  } else {
                    // No hero found, do full replacement
                    existingCollection.innerHTML = newCollectionContent.innerHTML;
                  }
                }
                
                // Hide loading overlay
                if (typeof window.LoadingManager !== 'undefined') {
                  window.LoadingManager.hideCollectionLoading();
                }
                
                // Reinitialize components
                this.reinitializeComponents();
                
              } else {
                throw new Error('No collection content found in response');
              }
            } else {
              throw new Error('Existing collection element not found');
            }
          } else {
            throw new Error(`HTTP Error: ${response.status}`);
          }
        } catch (error) {
          Utils.error(`Tab filter removal failed: ${error.message}`);
          if (typeof window.LoadingManager !== 'undefined') {
            window.LoadingManager.hideCollectionLoading();
          }
          
          // Fallback to full page navigation
          window.location.href = newUrl;
        }
  
        Utils.removeLocalStorage(CONFIG.localStorageKeys.filterValue);
      },
  
      async applyFilter(elem, collectionHandle, filterValue, filterId) {
        return this.debouncedApplyFilter(elem, collectionHandle, filterValue, filterId);
      },

      debouncedApplyFilter: Utils.debounce(async function(elem, collectionHandle, filterValue, filterId) {
        // Get reference to FilterBar object for proper context
        const filterBar = FilterBar;
        
        // Show loading state immediately
        if (typeof window.LoadingManager !== 'undefined') {
          window.LoadingManager.showCollectionLoading();
        }
        
        // Remove active class from all filters
        filterBar.filterCollectionBoxes.forEach(el => {
          el.classList.remove("active--filters");
        });

        // Add active class to clicked element
        elem.classList.add('active--filters');

        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        const newUrl = UrlUtils
          ? UrlUtils.buildUrlWithPreservedQuery(`/collections/${collectionHandle}/${filterValue}`, window.location.href)
          : `/collections/${collectionHandle}/${filterValue}`;
        
        try {
          // Update browser URL without page reload
          window.history.replaceState(null, null, newUrl);
          
          // Use optimized AJAX handler with caching
          const result = await AjaxHandler.getFilteredContent(newUrl);
          
          if (result && result.collection) {
            const existingCollection = document.querySelector('.collection');
            if (existingCollection) {
              // Try to preserve hero section and only update products efficiently
              const existingHero = existingCollection.querySelector('.collection-hero-top, .collection__hero, [class*="hero"]');
              const existingProducts = existingCollection.querySelector('.collection__main, .collection__products, .product-list, [class*="products"]');
              
              if (existingProducts && result.products && existingHero) {
                // Selective update: only replace products section, preserve hero
                Utils.updateDOMEfficiently(existingProducts, result.products.innerHTML);
              } else {
                // Fallback: full replacement but try to preserve hero
                if (existingHero) {
                  // Save hero content
                  const heroHTML = existingHero.outerHTML;
                  
                  // Replace entire collection content efficiently
                  Utils.updateDOMEfficiently(existingCollection, result.collection.innerHTML);
                  
                  // Try to restore hero at the beginning
                  requestAnimationFrame(() => {
                    const newHeroLocation = existingCollection.querySelector('.collection-hero-top, .collection__hero, [class*="hero"]');
                    if (!newHeroLocation) {
                      // Insert hero at the beginning if it doesn't exist in new content
                      existingCollection.insertAdjacentHTML('afterbegin', heroHTML);
                    }
                  });
                } else {
                  // No hero found, do full replacement
                  Utils.updateDOMEfficiently(existingCollection, result.collection.innerHTML);
                }
              }
              
              // Update other elements if available
              if (result.pagination) {
                const existingPagination = document.querySelector('.pagination, [data-pagination]');
                if (existingPagination) {
                  Utils.updateDOMEfficiently(existingPagination, result.pagination.innerHTML);
                }
              }
              
              if (result.productCount) {
                const existingCount = document.querySelector('.collection-header__count, [data-product-count]');
                if (existingCount) {
                  existingCount.textContent = result.productCount.textContent;
                }
              }
              
              // Save filter value to localStorage
              Utils.setLocalStorage(CONFIG.localStorageKeys.filterValue, filterValue);
              
              // Reinitialize components after DOM updates
              requestAnimationFrame(() => {
                filterBar.reinitializeComponents();
                if (typeof window.LoadingManager !== 'undefined') {
                  window.LoadingManager.hideCollectionLoading();
                }
              });
              
            } else {
              throw new Error('Existing collection element not found');
            }
          } else {
            throw new Error('No collection content found in response');
          }
        } catch (error) {
          Utils.error(`Tab filter failed: ${error.message}`);
          if (typeof window.LoadingManager !== 'undefined') {
            window.LoadingManager.hideCollectionLoading();
          }
          
          // Remove active state since filtering failed
          elem.classList.remove('active--filters');
          
          // Fallback to full page navigation only if it's a critical error
          if (!navigator.onLine || error.message.includes('HTTP 5')) {
            window.location.href = newUrl;
          }
        }
      }, 250),
  
      extractCollectionHandle() {
        const path = window.location.pathname;
        const match = path.match(/\/collections\/([^\/]+)/);
        return match ? match[1] : '';
      },
  
      // Share the updateContent method with tab filters
      updateContent(data) {
        if (typeof window.AjaxFilter !== 'undefined') {
          return window.AjaxFilter.updateContent(data);
        }
      },
  
      // Share the reinitializeComponents method with tab filters
      reinitializeComponents() {
        if (typeof window.AjaxFilter !== 'undefined') {
          return window.AjaxFilter.reinitializeComponents();
        }
      },
  
      // Share the waitForContentLoad method with tab filters
      waitForContentLoad(container) {
        if (typeof window.AjaxFilter !== 'undefined') {
          return window.AjaxFilter.waitForContentLoad(container);
        }
      }
    };
  
    // ========================================
    // LOADING MANAGER
    // ========================================
    // Moved to filter-ui.js - access via window.LoadingManager
    
    // ========================================
    // SELECTED FILTERS MANAGEMENT
    // ========================================
    // Moved to filter-ui.js - access via window.SelectedFiltersManager
    
    // ========================================
    // ACCORDION STATE MANAGEMENT
    // ========================================
    // Moved to filter-state.js - access via window.AccordionManager
    
    // ========================================
    // AJAX FILTER FUNCTIONALITY
    // ========================================
    
    // ========================================
    // FILTER SELECTION MANAGER (Multi-Select)
    // ========================================
    
    // FilterState moved to filter-state.js - access via window.FilterState
    
    const FilterSelectionManager = {
      // Use FilterState as single source of truth
      get selectedFilters() { 
        return typeof window.FilterState !== 'undefined' ? window.FilterState.selected : new Set(); 
      },
      get appliedFilters() { 
        return typeof window.FilterState !== 'undefined' ? window.FilterState.applied : new Set(); 
      },
      get selectedFilterUrls() { 
        return typeof window.FilterState !== 'undefined' ? window.FilterState.urls : new Map(); 
      },
      
      // Guard against double-fire: same tag toggled twice in quick succession (deselect → select again)
      _toggleGuard: {},
      _toggleGuardMs: 400,
      
      eventHandlers: {
        filterClick: null,
        applyClick: null
      },
      
      init() {
        this.initializeAppliedFilters();
        this.bindEvents();
        this.updateApplyButton();
      },
      
      initializeAppliedFilters() {
        // REPLACE state from DOM (don't add) so we don't accumulate stale tags after 5–10 interactions
        if (typeof window.FilterState !== 'undefined') {
          window.FilterState.clearAll();
        }
        
        const activeTags = document.querySelectorAll('.collection-filter__tag--applied, .collection-filter__tag--active');
        const processedTags = new Set();
        
        activeTags.forEach(tag => {
          const tagName = tag.getAttribute('data-tag-name');
          if (tagName && !processedTags.has(tagName)) {
            if (typeof window.FilterState !== 'undefined') {
              const link = tag.querySelector('a');
              const href = link ? link.getAttribute('href') : null;
              window.FilterState.addApplied(tagName, href);
            }
            processedTags.add(tagName);
            tag.classList.add('collection-filter__tag--active', 'collection-filter__tag--applied');
          }
        });
        
        this.updateApplyButton();
      },
  
      bindEvents() {
        this.unbindEvents();
        
        // Consolidated filter click handler - DRY principle
        const handleFilterClick = (event) => {
          const filterContainer = event.target.closest(CONFIG.selectors.collectionFilter);
          if (!filterContainer) return;
          
          // Find the tag item using multiple strategies
          const tagItem = this.findTagItem(event.target);
          if (!tagItem) return;
          
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Defer toggle to next tick so duplicate events (e.g. link + label) are coalesced
          const tagName = tagItem.getAttribute('data-tag-name');
          if (!tagName) return;
          const self = this;
          if (this._pendingToggle === tagName) return;
          this._pendingToggle = tagName;
          setTimeout(() => {
            self._pendingToggle = null;
            self.toggleFilterSelection(tagItem);
          }, 0);
          return false;
        };
        
        // Helper to find tag item from various click targets
        this.findTagItem = (target) => {
          // Strategy 1: Direct link click
          const link = target.closest(CONFIG.selectors.filterTags);
          if (link) {
            return link.closest(CONFIG.selectors.filterTagItems) || 
                   link.closest('li.collection-filter__tag');
          }
          
          // Strategy 2: Tag item click
          const tagItem = target.closest(CONFIG.selectors.filterTagItems);
          if (tagItem && tagItem.querySelector(CONFIG.selectors.filterTags)) {
            return tagItem;
          }
          
          // Strategy 3: Alternative selector
          return target.closest('li.collection-filter__tag');
        };
        
        // Store handler reference for cleanup
        this.eventHandlers.filterClick = handleFilterClick;
        document.addEventListener('click', handleFilterClick, true);

        // Keyboard: Enter/Space on filter tags (WCAG 2.1.1)
        const handleFilterKeydown = (event) => {
          const tagItem = this.findTagItem(event.target);
          if (!tagItem) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          const tagName = tagItem.getAttribute('data-tag-name');
          if (!tagName || this._pendingToggle === tagName) return;
          this._pendingToggle = tagName;
          const self = this;
          setTimeout(() => {
            self._pendingToggle = null;
            self.toggleFilterSelection(tagItem);
          }, 0);
        };
        this.eventHandlers.filterKeydown = handleFilterKeydown;
        document.addEventListener('keydown', handleFilterKeydown, true);

        // Handle Apply button click - consolidated handler
        const handleApplyClick = (event) => {
          const applyButton = this.findApplyButton(event.target);
          if (!applyButton) return;
          
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          if (!this.validateApplyClick(applyButton)) {
            return false;
          }
          
          // Store Apply button reference to maintain focus
          this._lastApplyButton = applyButton;
          this.applySelectedFilters();
          return false;
        };
        
        // Helper to find apply button from various click targets
        this.findApplyButton = (target) => {
          return target.closest(CONFIG.selectors.applyButton) ||
                 (target.closest(CONFIG.selectors.applyButtonText) && 
                  document.querySelector(CONFIG.selectors.applyButton)) ||
                 (target.closest(CONFIG.selectors.applyButtonCount) && 
                  document.querySelector(CONFIG.selectors.applyButton)) ||
                 (target.matches(CONFIG.selectors.applyButton) && target);
        };
        
        // Validate apply button click - allow when there are selections OR when there are changes (e.g. user deselected applied filters)
        this.validateApplyClick = (applyButton) => {
          if (applyButton.disabled) {
            return false;
          }
          const hasChanges = typeof window.FilterState !== 'undefined' ? window.FilterState.hasChanges() : false;
          if (this.selectedFilters.size > 0) return true;
          if (hasChanges) return true; // e.g. user deselected all → Apply clears filters
          return false;
        };
        
        // Store handler reference
        this.eventHandlers.applyClick = handleApplyClick;
        
        // Use document-level delegation so it works even after DOM updates
        document.addEventListener('click', handleApplyClick, false);
      },
      
      unbindEvents() {
        if (this.eventHandlers.filterClick) {
          document.removeEventListener('click', this.eventHandlers.filterClick, true);
        }
        if (this.eventHandlers.filterKeydown) {
          document.removeEventListener('keydown', this.eventHandlers.filterKeydown, true);
        }
        if (this.eventHandlers.applyClick) {
          document.removeEventListener('click', this.eventHandlers.applyClick, false);
        }
        this.eventHandlers.filterClick = null;
        this.eventHandlers.filterKeydown = null;
        this.eventHandlers.applyClick = null;
      },
      
      toggleFilterSelection(tagItem) {
        const tagName = tagItem.getAttribute('data-tag-name');
        if (!tagName) {
          Utils.error('No tag name found on filter item');
          return;
        }
        
        // Prevent double-fire: ignore same tag toggled again within a short window (fixes "deselect then auto-select again")
        const now = Date.now();
        const lastToggled = this._toggleGuard[tagName];
        if (lastToggled != null && (now - lastToggled) < this._toggleGuardMs) {
          return;
        }
        this._toggleGuard[tagName] = now;
        setTimeout(() => {
          delete this._toggleGuard[tagName];
        }, this._toggleGuardMs);
        
        // Get the link href for this tag
        const link = tagItem.querySelector('a');
        const href = link ? link.getAttribute('href') : null;
        
        // Check if this tag is currently applied (from server)
        const isApplied = tagItem.classList.contains('collection-filter__tag--applied') || 
                          tagItem.classList.contains('collection-filter__tag--active');
        const isCurrentlySelected = this.selectedFilters.has(tagName);

        const displayName = tagName.includes('_') ? tagName.split('_').slice(1).join(' ') : tagName;
        if (isCurrentlySelected) {
          if (typeof window.FilterState !== 'undefined') {
            window.FilterState.removeSelection(tagName);
          }
          tagItem.classList.remove('collection-filter__tag--selected', 'collection-filter__tag--active', 'collection-filter__tag--applied');
          tagItem.setAttribute('aria-pressed', 'false');
          if (typeof window.FilterAnnouncements !== 'undefined') {
            window.FilterAnnouncements.announce(`${displayName} removed from selection. ${this.selectedFilters.size} filter${this.selectedFilters.size !== 1 ? 's' : ''} selected. Click Apply to update.`);
          }
        } else {
          if (typeof window.FilterState !== 'undefined') {
            window.FilterState.addSelection(tagName, href);
          }
          tagItem.classList.add('collection-filter__tag--selected');
          tagItem.setAttribute('aria-pressed', 'true');
          if (typeof window.FilterAnnouncements !== 'undefined') {
            window.FilterAnnouncements.announce(`${displayName} selected. ${this.selectedFilters.size} filter${this.selectedFilters.size !== 1 ? 's' : ''} selected. Click Apply to update.`);
          }
        }
        this.updateApplyButton();
      },
      
      updateApplyButton() {
        const applyButton = document.querySelector(CONFIG.selectors.applyButton);
        const countElement = document.querySelector(CONFIG.selectors.applyButtonCount);
        const applyContainer = document.querySelector(CONFIG.selectors.applyContainer);
        
        if (!applyButton || !countElement || !applyContainer) {
          return;
        }

        const selectedCount = typeof window.FilterState !== 'undefined' ? window.FilterState.selectedCount : 0;
        const hasChanges = typeof window.FilterState !== 'undefined' ? window.FilterState.hasChanges() : false;

        // Show count when there are selected filters, or show as clickable when user only deselected (hasChanges, 0 selected)
        if (selectedCount > 0) {
          countElement.textContent = `(${selectedCount})`;
          countElement.style.display = 'inline-block';
          countElement.setAttribute('aria-label', `${selectedCount} filter${selectedCount !== 1 ? 's' : ''} selected`);
          if (hasChanges) {
            applyButton.classList.add('has-selections');
            applyContainer.classList.add('has-selections');
          } else {
            applyButton.classList.remove('has-selections');
            applyContainer.classList.remove('has-selections');
          }
        } else if (hasChanges) {
          // User deselected all applied filters – Apply should clear filters
          countElement.textContent = '';
          countElement.style.display = 'none';
          countElement.setAttribute('aria-label', 'Apply to remove filters');
          applyButton.classList.add('has-selections');
          applyContainer.classList.add('has-selections');
        } else {
          countElement.textContent = '';
          countElement.style.display = 'none';
          countElement.removeAttribute('aria-label');
          applyButton.classList.remove('has-selections');
          applyContainer.classList.remove('has-selections');
        }
      },
      
      // Get base collection URL
      getBaseCollectionUrl() {
        const pathParts = window.location.pathname.split('/').filter(part => part);
 
        if (pathParts.length >= 2 && pathParts[0] === 'collections') {
          return `/collections/${pathParts[1]}`;
        }
        
        const collectionElement = document.querySelector(CONFIG.selectors.collectionElement);
        if (collectionElement) {
          const handle = collectionElement.getAttribute('data-collection-handle') || 
                        collectionElement.getAttribute('data-handle');
          if (handle) {
            return `/collections/${handle}`;
          }
        }
        
        return '/collections';
      },
      
      // Extract tag handle from link href (most reliable)
      extractTagHandleFromLink(link, tagName) {
        if (!link || !link.href) return null;
        
        try {
          const linkUrl = new URL(link.href, window.location.origin);
          const linkPathParts = linkUrl.pathname.split('/').filter(part => part);
          
          if (linkPathParts.length >= 3 && linkPathParts[0] === 'collections') {
            const filterFromLink = linkPathParts.slice(2).join('/');
            // When link has multiple filters it's the current page URL – we can't tell which segment is this tag, so don't use it (caller will use extractTagHandleFromName)
            if (filterFromLink.includes('+')) {
              return null;
            }
            return filterFromLink;
          }
        } catch (e) {
        }
        
        return null;
      },
      
      // Fallback: extract tag handle from tag name format
      extractTagHandleFromName(tagName) {
        const tagNameParts = tagName.split('_');
        
        if (tagNameParts.length > 2) {
          // Compound: "Nail_Shape_Coffin" -> "nail-shape_coffin"
          const categoryParts = tagNameParts.slice(0, -1)
            .map(part => part.toLowerCase().replace(/\s+/g, '-'))
            .join('-');
          const valuePart = tagNameParts[tagNameParts.length - 1]
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
          return `${categoryParts}_${valuePart}`;
        }
        
        if (tagNameParts.length > 1) {
          // Two parts: "Shape_Coffin" -> "shape_coffin"
          const categoryPart = tagNameParts[0].toLowerCase().replace(/\s+/g, '-');
          const valuePart = tagNameParts[1]
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
          return `${categoryPart}_${valuePart}`;
        }
        
        // No category: handleize whole name
        return tagName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
      },
      
      // Extract tag handles from selected filters (always from tag name so link href can't be wrong/current-page)
      extractTagHandles(selectedTags) {
        const tagHandles = [];
        
        selectedTags.forEach(tagName => {
          const tagHandle = this.extractTagHandleFromName(tagName);
          if (tagHandle && !tagHandles.includes(tagHandle)) {
            tagHandles.push(tagHandle);
          }
        });
        
        return tagHandles.sort(); // Sort for consistent URLs
      },
      
      // Build filter URL from selected tags
      // Uses URLSearchParams for query parameters while preserving Shopify's path-based filter structure
      buildFilterUrl(baseUrl, selectedTags) {
        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;

        if (selectedTags.length === 0) {
          return UrlUtils
            ? UrlUtils.buildUrlWithPreservedQuery(baseUrl, window.location.href)
            : baseUrl;
        }
        
        // Always build from tag names so single selection isn't wrong (stored href can be stale multi-filter URL)
        const tagHandles = this.extractTagHandles(selectedTags);
        if (tagHandles.length === 0) {
          Utils.error('Failed to extract tag handles');
          return UrlUtils
            ? UrlUtils.buildUrlWithPreservedQuery(baseUrl, window.location.href)
            : baseUrl;
        }
        
        // Build path-based filter URL (Shopify format: /collections/handle/filter1+filter2)
        const tagsPath = tagHandles.join('+');
        const filterPath = `${baseUrl}/${tagsPath}`;

        return UrlUtils
          ? UrlUtils.buildUrlWithPreservedQuery(filterPath, window.location.href)
          : filterPath;
      },
      
      // Apply URL and update content
      async applyUrlAndUpdateContent(filterUrl) {
        // Store reference to Apply button before hiding overlay
        const applyButton = this._lastApplyButton || document.querySelector(CONFIG.selectors.applyButton);
        
        if (typeof window.FilterOverlay !== 'undefined') {
          // Hide overlay but prevent focus from moving to Show Filter button
          window.FilterOverlay.hide(true); // Pass flag to skip focus move
        }
        Utils.setLocalStorage(CONFIG.localStorageKeys.filterOpen, 'false');
        if (typeof window.LoadingManager !== 'undefined') {
          window.LoadingManager.showCollectionLoading();
        }
        if (typeof window.AccordionManager !== 'undefined') {
          window.AccordionManager.saveAccordionStates();
        }
        
        try {
          const pathOnly = filterUrl.split('?')[0];
          const pathParts = pathOnly.split('/').filter(part => part);
          const queryPart = filterUrl.includes('?') ? filterUrl.slice(filterUrl.indexOf('?')) : '';
          const currentPage = Math.max(1, parseInt(new URL(filterUrl, window.location.origin).searchParams.get('page') || '1', 10));
          let result;
          if (pathParts[0] === 'collections' && pathParts[1] && pathParts.length > 2) {
            const baseUrl = `/collections/${pathParts[1]}`;
            const tagSegment = pathParts.slice(2).join('/');
            const tags = tagSegment.split('+').filter(Boolean);
            if (tags.length > 1) {
              const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
              const singleTagUrls = UrlUtils
                ? UrlUtils.buildSingleTagFetchUrls(baseUrl, tags, filterUrl)
                : tags.map((tag) => baseUrl + '/' + tag + queryPart);
              result = await AjaxHandler.getFilteredContentOR(singleTagUrls, {
                basePath: pathOnly,
                currentPage,
                referenceUrl: filterUrl
              });
              const cache = window.__filterORCache;
              const neededForPage = currentPage * cache.perPage;
              if (cache && cache.products.length < neededForPage) {
                await AjaxHandler.ensureORCacheHasPage(cache, currentPage);
                result = AjaxHandler.buildORResultFromCache(cache, currentPage, result);
              }
            } else {
              window.__filterORCache = null;
              result = await AjaxHandler.getFilteredContent(filterUrl, false);
            }
          } else {
            window.__filterORCache = null;
            result = await AjaxHandler.getFilteredContent(filterUrl, false);
          }

          if (!result) {
            throw new Error('No content received from server');
          }
          
          window.history.replaceState(null, null, filterUrl);
          if (typeof window.AjaxFilter !== 'undefined') {
            await window.AjaxFilter.updateContentOptimized(result);
          }
          
          // Update state - sync applied from selected
          if (typeof window.FilterState !== 'undefined') {
            window.FilterState.syncAppliedFromSelected();
          }
          
          // Reinitialize components (skip syncing filter state FROM DOM – we already synced from selection above)
          requestAnimationFrame(() => {
            if (typeof window.AjaxFilter !== 'undefined') {
              window.AjaxFilter.reinitializeComponents({ skipFilterStateSync: true });
            }
            
            // Push our state to DOM; do NOT call initializeAppliedFilters() here or it re-adds deselected tags from server HTML
            requestAnimationFrame(() => {
              this.updateFilterVisualStates();
              
              if (typeof window.SelectedFiltersManager !== 'undefined') {
                window.SelectedFiltersManager.updateDisplay();
              }
              
              if (typeof window.LoadingManager !== 'undefined') {
                window.LoadingManager.hideCollectionLoading();
              }
              
              // Clear the stored Apply button reference after applying
              this._lastApplyButton = null;
              
              const countEl = document.querySelector('.collection-header__count, [data-product-count]');
              const countText = countEl ? countEl.textContent.trim() : '';
              const productList = document.querySelector('.collection__main') || document.querySelector('.collection__products') || document.querySelector('.product-list');
              const productCardSel = CONFIG.selectors.productCard || 'product-card, .product-card, [data-product-card], .card';
              const productCount = productList ? productList.querySelectorAll(productCardSel).length : 0;
              const message = countText
                ? `Results updated. ${countText}`
                : (productCount ? `Results updated. Showing ${productCount} products.` : 'Results updated.');
              if (typeof window.FilterAnnouncements !== 'undefined') {
                window.FilterAnnouncements.announce(message, 150);
              }
              document.dispatchEvent(new CustomEvent('filterContentLoaded', {
                detail: { url: filterUrl, result }
              }));
            });
          });
        } catch (error) {
          Utils.error('Failed to apply filters:', error);
          if (typeof window.LoadingManager !== 'undefined') {
            window.LoadingManager.hideCollectionLoading();
          }
          Utils.showError('Failed to load filtered products. Please try again.');
          
          if (!navigator.onLine || error.message.includes('HTTP 5')) {
            window.location.href = filterUrl;
          }
        }
      },
      
      // Main function - now much simpler
      async applySelectedFilters() {
        const baseUrl = this.getBaseCollectionUrl();
        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        
        // Handle no selections
        const selectedCount = typeof window.FilterState !== 'undefined' ? window.FilterState.selectedCount : 0;
        if (selectedCount === 0) {
          const clearedUrl = UrlUtils
            ? UrlUtils.buildClearedCollectionUrl(window.location.href)
            : baseUrl;
          await this.applyUrlAndUpdateContent(clearedUrl);
          if (typeof window.FilterState !== 'undefined') {
            window.FilterState.clearAll();
          }
          this.updateFilterVisualStates();
          return;
        }
        
        // Build filter URL
        const selectedTags = Array.from(this.selectedFilters);

        const filterUrl = this.buildFilterUrl(baseUrl, selectedTags);
        
        // Apply and update
        await this.applyUrlAndUpdateContent(filterUrl);
      },
      
      updateFilterVisualStates() {
        // Update all filter tags to reflect applied state
        const allTags = document.querySelectorAll(CONFIG.selectors.filterTagItems);
        allTags.forEach(tag => {
          const tagName = tag.getAttribute('data-tag-name');
          if (!tagName) return;
          
          const isSelected = this.appliedFilters.has(tagName);
          
          if (isSelected) {
            tag.classList.add('collection-filter__tag--active', 'collection-filter__tag--applied');
            tag.classList.remove('collection-filter__tag--selected');
            tag.setAttribute('aria-pressed', 'true');
          } else {
            tag.classList.remove('collection-filter__tag--active', 'collection-filter__tag--applied', 'collection-filter__tag--selected');
            tag.setAttribute('aria-pressed', 'false');
          }
        });
        
        this.updateApplyButton();
      },
      
      clearSelections() {
        if (typeof window.FilterState !== 'undefined') {
          window.FilterState.clearAll();
        }
        
        const allTags = document.querySelectorAll(CONFIG.selectors.filterTagItems);
        allTags.forEach(tag => {
          tag.classList.remove('collection-filter__tag--selected', 'collection-filter__tag--active', 'collection-filter__tag--applied');
          tag.setAttribute('aria-pressed', 'false');
        });
        
        this.updateApplyButton();
      }
    };

    // ========================================
    // AJAX FILTER FUNCTIONALITY
    // ========================================
    // Moved to filter-ajax.js - access via window.AjaxFilter
  
    // ========================================
    // EVENT HANDLERS
    // ========================================
    // Moved to filter-ui.js - access via window.EventHandlers
    
    // ========================================
    // INITIALIZATION
    // ========================================
    
    const CollectionFilterSystem = {
      _initialized: false,
      init() {
        // Prevent double init (DOMContentLoaded + load both call init – avoids duplicate handlers / flaky toggles)
        if (this._initialized) return;
        this._initialized = true;
        
        // Check dependencies
        if (typeof $ === 'undefined') {
          Utils.error('jQuery is not loaded! Some features may not work properly.');
        }
        
        // Initialize components
        this.initializeState();
        SortManager.init();
        FilterBar.init();
        // Initialize FilterSelectionManager BEFORE AjaxFilter to ensure it handles clicks
        FilterSelectionManager.init();
        if (typeof window.AjaxFilter !== 'undefined') {
          window.AjaxFilter.init();
        }
        if (typeof window.SelectedFiltersManager !== 'undefined') {
          window.SelectedFiltersManager.init();
        }
        if (typeof window.AccordionManager !== 'undefined') {
          window.AccordionManager.bindAccordionEvents();
          window.AccordionManager.restoreAccordionStates();
        }
        if (typeof window.EventHandlers !== 'undefined') {
          window.EventHandlers.init();
        }
        this.bindGlobalEvents();
        // When URL is opened directly with multiple tags (OR), combine tags and load products (same as Apply does)
        let _orDirectLoadDone = false;
        const runORLogicOnDirectLoad = () => {
          if (_orDirectLoadDone) return;
          const path = window.location.pathname;
          const pathParts = path.split('/').filter(Boolean);
          if (pathParts[0] !== 'collections' || !pathParts[1] || pathParts.length <= 2) return;
          const tagSegment = pathParts.slice(2).join('/');
          if (!tagSegment.includes('+')) return;
          _orDirectLoadDone = true;
          const baseUrl = `/collections/${pathParts[1]}`;
          const tagsFromUrl = tagSegment.split('+').filter(Boolean);
          const wrapper = document.querySelector('.collection-filter__groups-wrapper');
          const normalize = (s) => (s || '').toLowerCase().replace(/-/g, '_');
          if (typeof window.FilterState !== 'undefined') {
            window.FilterState.clearSelections();
            window.FilterState.clearApplied();
          }
          if (wrapper) {
            const allTagEls = wrapper.querySelectorAll('.collection-filter__tag');
            tagsFromUrl.forEach(tagNameFromUrl => {
              const normUrl = normalize(tagNameFromUrl);
              for (const tagEl of allTagEls) {
                const name = tagEl.getAttribute('data-tag-name');
                const handle = tagEl.getAttribute('data-tag-handle');
                if (normUrl === normalize(name) || normUrl === normalize(handle)) {
                  tagEl.classList.add('collection-filter__tag--selected');
                  tagEl.setAttribute('aria-pressed', 'true');
                  if (typeof window.FilterState !== 'undefined') {
                    const domTagName = name || tagNameFromUrl;
                    const href = tagEl.querySelector('a')?.getAttribute('href') || baseUrl + '/' + (handle || tagNameFromUrl);
                    window.FilterState.addSelection(domTagName, href);
                  }
                  break;
                }
              }
            });
            if (typeof window.FilterState !== 'undefined') {
              window.FilterState.syncAppliedFromSelected();
            }
          } else if (typeof window.FilterState !== 'undefined') {
            tagsFromUrl.forEach(tag => {
              window.FilterState.addSelection(tag, baseUrl + '/' + tag);
            });
            window.FilterState.syncAppliedFromSelected();
          }
          const fullUrl = window.location.pathname + window.location.search;
          FilterSelectionManager.applyUrlAndUpdateContent(fullUrl);
        };
        requestAnimationFrame(() => runORLogicOnDirectLoad());
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => {
            setTimeout(runORLogicOnDirectLoad, 150);
          }, { once: true });
        }
      },

      initializeState() {
        // Remove opacity-low from show-filter button on load so it displays at full opacity
        const showFilterBtn = document.getElementById('show_filter');
        if (showFilterBtn) showFilterBtn.classList.remove('opacity-low');

        const shouldBeOpen = Utils.getLocalStorage(CONFIG.localStorageKeys.filterOpen) === 'true';
        
        if (shouldBeOpen) {
          if (typeof window.FilterOverlay !== 'undefined') {
            window.FilterOverlay.show();
            if (Utils.isDesktop()) {
              window.FilterOverlay.setFreeze();
            }
          }
        } else {
          const filter = document.querySelector(CONFIG.selectors.collectionFilter);
          if (filter) {
            filter.classList.remove('show');
            filter.setAttribute('inert', '');
            filter.setAttribute('aria-hidden', 'true');
          }
          // Ensure body scroll is unlocked if filter is closed
          if (typeof window.FilterOverlay !== 'undefined') {
            window.FilterOverlay.unlockBodyScroll();
          }
        }
        
        // Update selected filters display on page load
        // Use requestAnimationFrame for optimal DOM update timing
        const updateSelectedFilters = () => {
          if (typeof window.SelectedFiltersManager !== 'undefined') {
            window.SelectedFiltersManager.updateDisplay();
          }
        };
        
        // Single RAF for selected filters update (reduced nesting)
        requestAnimationFrame(updateSelectedFilters);
        if (document.readyState !== 'complete') {
          window.addEventListener('load', function() {
            requestAnimationFrame(updateSelectedFilters);
          }, { once: true });
        }
      },

      _eventHandlers: [],

      bindGlobalEvents() {
        var self = this;
        var paginationHandler = function(event) {
      const paginationLink = event.target.closest('a[href*="page="], .pagination__link, .pagination a, a[href*="/page/"]');
      if (!paginationLink || !paginationLink.href) return;

      const cache = window.__filterORCache;
      const currentPath = window.location.pathname;
      const linkUrl = new URL(paginationLink.href, window.location.origin);
      const pageParam = linkUrl.searchParams.get('page');
      const targetPage = pageParam ? parseInt(pageParam, 10) : (linkUrl.pathname === currentPath ? 1 : null);

      if (cache && cache.basePath === currentPath && targetPage >= 1) {
        event.preventDefault();
        event.stopPropagation();

        const neededCount = targetPage * cache.perPage;
        const hasEnough = cache.products.length >= neededCount;
        const canFetchMore = cache.tagUrls && cache.tagHasMore && cache.tagHasMore.some(Boolean);

        if (typeof window.LoadingManager !== 'undefined') window.LoadingManager.showCollectionLoading();
        if (typeof document.documentElement.dispatchEvent === 'function') {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        }
        const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
        const paginationRef = cache.referenceUrl || window.location.href;
        const newUrl = UrlUtils
          ? UrlUtils.buildUrlWithAllQuery(cache.basePath, paginationRef, { page: targetPage })
          : (targetPage <= 1 ? cache.basePath : cache.basePath + (cache.basePath.includes('?') ? '&' : '?') + 'page=' + targetPage);
        window.history.pushState(null, null, newUrl);

        const renderPage = () => {
          cache.total = cache.products.length;
          const totalPages = Math.max(1, Math.ceil(cache.total / cache.perPage));
          const safePage = Math.min(targetPage, totalPages);
          const pageParts = AjaxHandler.sliceORItemsForPage(cache.products, safePage, cache.perPage);
          const existingProducts = document.querySelector('.collection__main') || document.querySelector('.collection__products') || document.querySelector('.product-list');
          const productListEl = existingProducts && existingProducts.querySelector('product-list');
          let inner = pageParts.join('\n');
          if (productListEl) {
            const mobileLayout = productListEl.getAttribute('collection-mobile-layout') || 'medium';
            const desktopLayout = productListEl.getAttribute('collection-desktop-layout') || 'medium';
            inner = `<product-list class="product-list" collection-mobile-layout="${mobileLayout}" collection-desktop-layout="${desktopLayout}">${inner}</product-list>`;
          }
          const paginationHTML = AjaxHandler.buildORNavigationHTML(cache.total, safePage, cache.basePath, cache.referenceUrl);
          if (paginationHTML) inner = inner + '\n' + paginationHTML;
          if (existingProducts) Utils.updateDOMEfficiently(existingProducts, inner);
          requestAnimationFrame(() => {
            if (typeof window.LoadingManager !== 'undefined') window.LoadingManager.hideCollectionLoading();
            if (typeof document.documentElement.dispatchEvent === 'function') {
              document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
            }
            if (typeof window.AjaxFilter !== 'undefined' && window.AjaxFilter.reinitializeComponents) window.AjaxFilter.reinitializeComponents();
          });
        };

        if (hasEnough) {
          setTimeout(renderPage, 60);
          return;
        }

        (async function fetchMoreThenRender() {
          let seenKeys = cache.seenKeys;
          if (!seenKeys || !(seenKeys instanceof Set)) {
            seenKeys = new Set();
            cache.products.forEach((html, i) => {
              const el = document.createElement('div');
              el.innerHTML = html;
              seenKeys.add(AjaxHandler.getProductDedupeKey(el.firstElementChild || el, i));
            });
            cache.seenKeys = seenKeys;
          }
          while (cache.products.length < neededCount && cache.tagHasMore && cache.tagHasMore.some(Boolean)) {
            const urlsToFetch = [];
            for (let i = 0; i < cache.tagUrls.length; i++) {
              if (!cache.tagHasMore[i]) continue;
              const base = cache.tagUrls[i];
              const nextPage = (cache.tagPageNumbers[i] || 1) + 1;
              const UrlUtilsRef = window.FilterUtils && window.FilterUtils.UrlUtils;
              const pageUrl = UrlUtilsRef
                ? UrlUtilsRef.buildUrlWithAllQuery(base.split('?')[0], base, { page: nextPage })
                : (base.includes('?') ? base.replace(/([?&])page=\d+/, '$1page=' + nextPage) : base + (base.includes('?') ? '&' : '?') + 'page=' + nextPage);
              urlsToFetch.push({ index: i, url: pageUrl });
            }
            const results = await Promise.all(urlsToFetch.map(({ url }) => AjaxHandler.getFilteredContent(url, false)));
            results.forEach((result, idx) => {
              const i = urlsToFetch[idx].index;
              if (!result || !result.products) {
                cache.tagHasMore[i] = false;
                return;
              }
              AjaxHandler.mergeProductCardsFromResult(result, seenKeys, cache.products);
              cache.tagPageNumbers[i] = (cache.tagPageNumbers[i] || 1) + 1;
              const nextUrl = AjaxHandler.getNextPageUrl(result);
              const totalPages = AjaxHandler.getTotalPagesFromResult(result);
              cache.tagHasMore[i] = !!nextUrl || totalPages > cache.tagPageNumbers[i];
            });
            if (results.every(r => !r || !r.products)) break;
          }
          renderPage();
        })();
        return;
      }

      const currentUrl = new URL(window.location.href);
      const currentPathParts = currentUrl.pathname.split('/').filter(part => part);
      const linkPathParts = linkUrl.pathname.split('/').filter(part => part);
      if (currentPathParts.length > 2 && currentPathParts[0] === 'collections') {
        const collectionHandle = currentPathParts[1];
        const filterSegments = currentPathParts.slice(2);
        if (linkPathParts.length === 2 && linkPathParts[0] === 'collections' && linkPathParts[1] === collectionHandle) {
          const newPath = `/collections/${collectionHandle}/${filterSegments.join('/')}`;
          linkUrl.pathname = newPath;
          paginationLink.href = linkUrl.href;
        }
      }
        };
        document.addEventListener('click', paginationHandler, true);
        this._eventHandlers.push({ target: document, type: 'click', handler: paginationHandler, capture: true });

        var popstateHandler = function() {
      const cache = window.__filterORCache;
      const path = window.location.pathname;
      const page = Math.max(1, parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10));
      if (cache && cache.basePath === path && cache.products && cache.products.length) {
        const totalPages = Math.ceil(cache.total / cache.perPage);
        const targetPage = Math.min(page, totalPages);
        const pageParts = AjaxHandler.sliceORItemsForPage(cache.products, targetPage, cache.perPage);
        const existingProducts = document.querySelector('.collection__main') || document.querySelector('.collection__products') || document.querySelector('.product-list');
        const productListEl = existingProducts && existingProducts.querySelector('product-list');
        let inner = pageParts.join('\n');
        if (productListEl) {
          const mobileLayout = productListEl.getAttribute('collection-mobile-layout') || 'medium';
          const desktopLayout = productListEl.getAttribute('collection-desktop-layout') || 'medium';
          inner = `<product-list class="product-list" collection-mobile-layout="${mobileLayout}" collection-desktop-layout="${desktopLayout}">${inner}</product-list>`;
        }
        const paginationHTML = AjaxHandler.buildORNavigationHTML(cache.total, targetPage, cache.basePath, cache.referenceUrl);
        if (paginationHTML) inner = inner + '\n' + paginationHTML;
        if (existingProducts) Utils.updateDOMEfficiently(existingProducts, inner);
      }
      requestAnimationFrame(function() {
        if (typeof window.SelectedFiltersManager !== 'undefined') {
          window.SelectedFiltersManager.updateDisplay();
        }
      });
        };
        window.addEventListener('popstate', popstateHandler);
        this._eventHandlers.push({ target: window, type: 'popstate', handler: popstateHandler });

        var pageshowHandler = function() {
          if (typeof window.SelectedFiltersManager !== 'undefined') {
            window.SelectedFiltersManager.updateDisplay();
          }
        };
        window.addEventListener('pageshow', pageshowHandler);
        this._eventHandlers.push({ target: window, type: 'pageshow', handler: pageshowHandler });

        var facetUpdateHandler = function(event) {
          var sectionId = event && event.detail && event.detail.url && event.detail.url.searchParams && event.detail.url.searchParams.get('section_id');
          var targetSection =
            (sectionId && document.getElementById('shopify-section-' + sectionId)) ||
            document.querySelector('.shopify-section--main-collection') ||
            document.querySelector('.shopify-section');
          if (!targetSection) return;
          var observer = new MutationObserver(function() {
            observer.disconnect();
            var showFilterBtn = document.getElementById('show_filter');
            if (showFilterBtn) showFilterBtn.classList.remove('opacity-low');
            if (typeof window.SelectedFiltersManager !== 'undefined') {
              window.SelectedFiltersManager.bindEvents();
              window.SelectedFiltersManager.updateDisplay();
            }
            if (typeof window.EventHandlers !== 'undefined') {
              window.EventHandlers.bindShowFilter();
            }
            if (typeof window.AccordionManager !== 'undefined') {
              window.AccordionManager.restoreAccordionStates();
            }
          });
          observer.observe(targetSection, { childList: true, subtree: true });
        };
        document.addEventListener('facet:update', facetUpdateHandler);
        this._eventHandlers.push({ target: document, type: 'facet:update', handler: facetUpdateHandler });

        var loadHandler = function() {
          CollectionFilterSystem.init();
        };
        window.addEventListener('load', loadHandler);
        this._eventHandlers.push({ target: window, type: 'load', handler: loadHandler });

        if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
          document.addEventListener('shopify:section:unload', function() {
            CollectionFilterSystem.destroy();
          });
        }
      },

      destroy: function() {
        this._eventHandlers.forEach(function(ref) {
          var target = ref.target;
          var type = ref.type;
          var handler = ref.handler;
          if (ref.capture) {
            target.removeEventListener(type, handler, true);
          } else {
            target.removeEventListener(type, handler);
          }
        });
        this._eventHandlers = [];
        if (window.__filterORCache) window.__filterORCache = null;
        if (window.CacheManager && window.CacheManager.clear) window.CacheManager.clear();
        this._initialized = false;
      }
    };

    // ========================================
    // AUTO-INITIALIZATION
    // ========================================
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        CollectionFilterSystem.init();
      });
    } else {
      CollectionFilterSystem.init();
    }

    window.CollectionFilterSystem = CollectionFilterSystem;

    // ========================================
    // GLOBAL EXPORTS
    // ========================================
    // Global triggerFilter function for backward compatibility
    window.triggerFilter = async function(elem, collectionHandle, filterValue) {
      
      // Show loading overlay
      if (typeof window.LoadingManager !== 'undefined') {
        window.LoadingManager.showCollectionLoading();
      }
      
      // Use the FilterBar's applyFilter method
      if (elem.classList.contains('active--filters')) {
        await FilterBar.removeFilter(elem, collectionHandle);
      } else {
        await FilterBar.applyFilter(elem, collectionHandle, filterValue, filterValue);
      }
    };

    // Optimized debounced version for better performance
    window.debouncedTriggerFilter = Utils.debounce(async function(elem, collectionHandle, filterValue) {
      
      // Cancel any previous requests
      AjaxHandler.cancelAllRequests();
      
      // Show loading with skeleton screen
      if (typeof window.LoadingManager !== 'undefined') {
        window.LoadingManager.showCollectionLoading();
      }
      const productsWrapper = document.querySelector('.collection__products-wrapper');
      if (productsWrapper) {
        productsWrapper.classList.add('filter-transition-fast');
      }
      
      // Use the FilterBar's applyFilter method with caching enabled
      if (elem.classList.contains('active--filters')) {
        await FilterBar.removeFilter(elem, collectionHandle);
      } else {
        await FilterBar.applyFilter(elem, collectionHandle, filterValue, filterValue);
      }
      
      // Add progressive loading class for smooth animations
      if (productsWrapper) {
        productsWrapper.classList.add('progressive-loading');
        requestAnimationFrame(function() {
          productsWrapper.classList.remove('progressive-loading', 'filter-transition-fast');
        });
      }
    }, 300);
    
    // Slick slider function for backward compatibility
    window.slickslider = function() {
      if (typeof $ !== 'undefined' && typeof $.fn.slick !== 'undefined') {
        $(".filter-bar").slick({
          slidesToShow: 2,
          dots: true,
          mobileFirst: true,
          prevArrow: '<button class="slick-arrow slick-prev" aria-label="Previous filter"><svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#FFFFFF"></circle><path d="M21 12L13 18.5L21 25" stroke="#B6637B" stroke-width="3" stroke-linecap="round"></path></svg></button>',
          nextArrow: '<button class="slick-arrow slick-next" aria-label="Next filter"><svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#FFFFFF"></circle><path d="M21 12L13 18.5L21 25" stroke="#B6637B" stroke-width="3" stroke-linecap="round"></path></svg></button>',
          responsive: [{
            breakpoint: 768,
            settings: 'unslick',
            slidesToShow: 2,
          }]
        });
  
        $('.filter--bar__container').show();
      }
    };
    
    // Make functions globally available for integration
    window.CollectionFilterSystem = CollectionFilterSystem;
    window.filterBar = FilterBar.initializeFilterBarComponents;
    // FilterOverlay, SelectedFiltersManager, LoadingManager, EventHandlers are exposed from filter-ui.js
    window.SelectedFilterManager = window.SelectedFiltersManager; // Alias for backward compatibility with onclick handlers
    window.FilterSelectionManager = FilterSelectionManager;
  
    // ========================================
    // PERFORMANCE MONITORING & CLEANUP
    // ========================================
    
    // Periodic cache cleanup
    setInterval(() => {
      CacheManager.cleanup();
    }, 60000); // Clean every minute
    
    // Performance monitoring
    const PerformanceMonitor = {
      startTime: null,
      
      start: () => {
        PerformanceMonitor.startTime = performance.now();
      },
      
      end: (label = 'Filter operation') => {
        if (PerformanceMonitor.startTime) {
          const duration = performance.now() - PerformanceMonitor.startTime;
          PerformanceMonitor.startTime = null;
          return duration;
        }
      }
    };
    
    // Monitor filter operations
    const originalDebouncedApplyFilter = FilterBar.debouncedApplyFilter;
    FilterBar.debouncedApplyFilter = Utils.debounce(async function(...args) {
      PerformanceMonitor.start();
      const result = await originalDebouncedApplyFilter.apply(this, args);
      PerformanceMonitor.end('Tab filter');
      return result;
    }, 250);
    
    // Prefetch common filter results on page load
    document.addEventListener('DOMContentLoaded', () => {
      const filterBoxes = document.querySelectorAll('.filter-collection-box');
      const currentUrl = window.location.pathname;
      
      // Prefetch first few filters after page is idle
      // Use requestIdleCallback if available, fallback to requestAnimationFrame
      const prefetchFilters = () => {
        filterBoxes.forEach((box, index) => {
          if (index < 3) { // Only prefetch first 3 filters
            const collectionHandle = box.dataset.collectionHandle;
            const filterValue = box.dataset.filterValue;
            if (collectionHandle && filterValue) {
              const url = `/collections/${collectionHandle}/${filterValue}`;
              if (url !== currentUrl) {
                Utils.preloadResource(url);
              }
            }
          }
        });
      };
      
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetchFilters, { timeout: 2000 });
      } else {
        // Fallback: use requestAnimationFrame chain for delayed execution
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(prefetchFilters);
          });
        });
      }
    });
    
  })();
  
  //filter logic for collection filter.